import { useCallback, useMemo } from "react";
import { Contact as ContactIcon, Plus } from "lucide-react";
import { useNavigate } from "react-router";
import { useLingui } from "@lingui/react";
import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { SegmentedToggle } from "@/components/ui/segmented-toggle";
import { QueryErrorState } from "@/components/ui/query-error-state";
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/table/EmptyState";
import { ContactsTable } from "@/components/table/ContactsTable";
import { useContactsPaginated, useUpdateContactField, type ContactsPaginatedOptions } from "@/hooks/useContacts";
import { useListUrlState } from "@/hooks/useListUrlState";
import { fetchAllPages } from "@/hooks/fetchAllPages";
import { OpenAPI } from "@/api/core/OpenAPI";
import { request as __request } from "@/api/core/request";
import type { contacts_ContactListResponse } from "@/api/models/contacts_ContactListResponse";
import type { Contact } from "@/types/contact";
import type { models_Contact } from "@/types";

const SCOPES = ["current", "all"] as const;
type ContactsScope = (typeof SCOPES)[number];
type Audience = "parents" | "all";
// `audience=all` lists every contact; without it the list shows parents only.
// Only filters the list endpoint applies are kept; a chip that changes nothing would mislead.
const FILTER_KEYS = ["status", "labels", "audience"] as const;
const DEFAULT_SORT: SortingState = [{ id: "lastname", desc: false }];

// Map frontend column IDs to backend sort_by values
const SORT_COLUMN_MAP: Record<string, string> = {
  firstname: 'firstname',
  lastname: 'lastname',
  emailMain: 'email',
  status: 'status',
  createdAt: 'created_at',
  contactInternalId: 'internal_id',
};

const ContactsPage = () => {
  const navigate = useNavigate();
  const { i18n } = useLingui();

  // Search, page, sort, scope and filters live in the URL, so Back from a
  // contact returns to the same filtered page.
  const list = useListUrlState<ContactsScope, (typeof FILTER_KEYS)[number]>({
    scopes: SCOPES,
    defaultScope: "current",
    filterKeys: FILTER_KEYS,
    defaultSort: DEFAULT_SORT,
  });
  const audience: Audience = list.filters.audience?.includes("all") ? "all" : "parents";

  const sortEntry = list.sort[0];
  const queryOptions: ContactsPaginatedOptions = useMemo(() => ({
    status: list.filters.status?.join(","),
    labelIds: list.filters.labels?.join(","),
    search: list.search || undefined,
    sortBy: (sortEntry && SORT_COLUMN_MAP[sortEntry.id]) || "lastname",
    sortOrder: sortEntry?.desc ? "desc" : "asc",
    parentsOnly: audience === "parents",
    withContracts: list.scope === "current",
  }), [list.filters.status, list.filters.labels, list.search, sortEntry, audience, list.scope]);

  const {
    data: contacts,
    total,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useContactsPaginated(list.pageSize, queryOptions, list.pagination);

  // The table seeds its filter chips from the URL once; `key={list.syncKey}`
  // re-mounts it when the URL changes from outside (sidebar, palette, Back).
  const initialFilters: ColumnFiltersState = (["status", "labels"] as const).flatMap((key) =>
    list.filters[key] ? [{ id: key, value: list.filters[key] }] : []
  );

  const { setFilters, setSort } = list;
  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    const values = (id: string) => {
      const value = filters.find((f) => f.id === id)?.value;
      return Array.isArray(value) && value.length > 0 ? (value as string[]) : undefined;
    };
    setFilters((prev) => ({ ...prev, status: values("status"), labels: values("labels") }));
  }, [setFilters]);

  const setAudience = useCallback((next: Audience) => {
    setFilters((prev) => ({ ...prev, audience: next === "all" ? ["all"] : undefined }));
  }, [setFilters]);

  // Columns the API cannot sort by are ignored rather than sorting one page.
  const handleSortingChange = useCallback((sorting: SortingState) => {
    const serverSortable = sorting.filter((s) => SORT_COLUMN_MAP[s.id]);
    setSort(serverSortable.length > 0 ? serverSortable : DEFAULT_SORT);
  }, [setSort]);

  // "Export all matching" pages through the same query the list shows.
  const exportAll = useCallback(
    () =>
      fetchAllPages<Contact>(async (limit, offset) => {
        const response = await __request<contacts_ContactListResponse>(OpenAPI, {
          method: "GET",
          url: "/api/v1/contacts",
          query: {
            limit,
            offset,
            status: queryOptions.status,
            label_ids: queryOptions.labelIds,
            search: queryOptions.search,
            sort_by: queryOptions.sortBy,
            sort_order: queryOptions.sortOrder,
            parents_only: queryOptions.parentsOnly,
            with_contracts: queryOptions.withContracts,
          },
        });
        return (response.contacts ?? []) as Contact[];
      }, 500),
    [queryOptions]
  );

  const updateContactFieldMutation = useUpdateContactField();

  const handleCellUpdate = async (
    rowId: string,
    field: string,
    newValue: models_Contact[keyof models_Contact]
  ) => {
    await updateContactFieldMutation.mutateAsync({
      id: rowId,
      field: field as keyof models_Contact,
      value: newValue,
    });
  };

  const addContact = useCallback(() => navigate("/contacts/create"), [navigate]);
  const addContactLabel = i18n._({ id: "contacts.list.add", message: "Add contact" });
  const narrowed = list.scope === "current" || audience === "parents";

  const emptyState = (
    <EmptyState
      icon={<ContactIcon className="h-10 w-10" />}
      title={
        narrowed
          ? i18n._({
              id: "contacts.list.emptyNarrowed.title",
              message: "No contacts in this view",
            })
          : i18n._({ id: "contacts.list.empty.title", message: "No contacts yet" })
      }
      action={
        <div className="flex flex-wrap justify-center gap-2">
          {narrowed && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                list.setScope("all");
                setAudience("all");
              }}
            >
              {i18n._({ id: "contacts.list.showAll", message: "Show all contacts" })}
            </Button>
          )}
          <Button size="sm" onClick={addContact}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addContactLabel}
          </Button>
        </div>
      }
    />
  );

  // The error shows inside the table body: a stale or shared URL filter the
  // API rejects stays visible in the toolbar and can be removed.
  const loadError =
    error && !isLoading ? (
      <QueryErrorState
        error={error}
        onRetry={() => void refetch()}
        action={
          list.isFiltered ? (
            <Button type="button" variant="outline" size="sm" onClick={list.reset}>
              {i18n._({ id: "table.filter.clearAll", message: "Clear all filters" })}
            </Button>
          ) : undefined
        }
      />
    ) : undefined;

  return (
    <PageContainer width="list" className="flex h-full flex-col">
      <PageHeader
        title={i18n._({ id: "nav.familiesContacts", message: "Families & Contacts" })}
        actions={
          <Button onClick={addContact}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {addContactLabel}
          </Button>
        }
      />

      <div className="min-h-0 flex-1">
        <ContactsTable
          key={list.syncKey}
          data={loadError ? [] : contacts}
          isLoading={isLoading}
          errorState={loadError}
          onCellUpdate={handleCellUpdate}
          enableSelection={true}
          serverSidePagination={true}
          totalRows={loadError ? 0 : total}
          pagination={list.pagination}
          onPaginationChange={list.setPagination}
          isFetching={isFetching}
          initialFilters={initialFilters}
          onFilterChange={handleFilterChange}
          sorting={list.sort}
          onSortingChange={handleSortingChange}
          searchTerm={list.search}
          onSearchChange={list.setSearch}
          hasExternalFilters={
            list.search !== "" || !!list.filters.status || !!list.filters.labels
          }
          exportAll={exportAll}
          emptyState={emptyState}
          toolbarRight={
            <div className="ml-auto flex flex-wrap gap-2">
              <SegmentedToggle<Audience>
                value={audience}
                onValueChange={setAudience}
                aria-label={i18n._({ id: "contacts.filter.audienceLabel", message: "Which contacts" })}
                options={[
                  { value: "parents", label: i18n._({ id: "contacts.filter.parents", message: "Parents" }) },
                  { value: "all", label: i18n._({ id: "contacts.filter.allContacts", message: "All" }) },
                ]}
              />
              <SegmentedToggle<ContactsScope>
                value={list.scope ?? "current"}
                onValueChange={list.setScope}
                aria-label={i18n._({ id: "contacts.filter.scopeLabel", message: "Contract" })}
                options={[
                  { value: "current", label: i18n._({ id: "contacts.filter.current", message: "Current" }) },
                  { value: "all", label: i18n._({ id: "contacts.filter.all", message: "All" }) },
                ]}
              />
            </div>
          }
        />
      </div>
    </PageContainer>
  );
};

export default ContactsPage;
